import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, map, Observable, of, ReplaySubject } from 'rxjs';
import { Report } from './component/report';





@Injectable({ providedIn: 'root' })
export class ReportService {
    private apiLink = environment.apiLink
    private _httpClient = inject(HttpClient);

    constructor() { }

    getAll(
        page: number,
        size: number,
        agentId?: number,
        name?: string,
        isShared?: boolean,
        ownedBy?: { id: number; role: string }
    ) {
        let params = new HttpParams();
        params = params.append('offset', page)
        params = params.append('limit', size)

        if (agentId) {
            params = params.append('agentId', agentId)
        }
        if (name) {
            params = params.append('name', name)
        }
        if (isShared === true || isShared === false) {
            params = params.append('isShared', String(isShared))
        }
        if (ownedBy && Number.isFinite(ownedBy.id) && ownedBy.role) {
            params = params.append('ownedById', String(ownedBy.id));
            params = params.append('ownedByRole', ownedBy.role);
        }
        return this._httpClient.get(this.apiLink + 'report/get-reports', { params }).pipe(
            map((res: any) => {
                return ({ data: res.data.reports, count: res.data.count })
            }),
        );
    }

    getById(id: any): Observable<Report> {
        let params = new HttpParams();
        params = params.append('reportId', id)
        return this._httpClient.get<Report>(this.apiLink + 'report/get-report', { params }).pipe(
            map((res: any) => {
                return res.data.report
            }),
        );
    }

    /** Non-legacy reports: API returns a full HTML document (text/html). */
    getReportHtml(id: string) {
        const params = new HttpParams()
            .append('report_id', id)
            .append('reportId', id);
        return this._httpClient.get(this.apiLink + 'report/get-report', {
            params,
            responseType: 'text',
        });
    }

    /**
     * Translated report HTML by id. Returns a full HTML document (text/html).
     * The `x-lang` header is attached by `authInterceptor`; `lang` selects the target
     * language and `force` controls whether a cached translation may be reused.
     */
    getReportHtmlForTranslation(
        id: string,
        lang: string,
        force = false
    ): Observable<string> {
        const params = new HttpParams()
            .append('reportId', id)
            .append('lang', lang)
            .append('force', String(force));
        return this._httpClient.get(
            this.apiLink + 'report/get-report-by-id-for-translation',
            { params, responseType: 'text' }
        );
    }

    /**
     * Fetch a pre-generated translation document by its direct `report/get-file` URL.
     * Must go through `HttpClient` (never a raw `iframe.src`/`location.replace`) so
     * `authInterceptor` attaches the `Authorization` header — the browser does not send
     * app headers on a plain cross-origin navigation.
     */
    getReportHtmlByUrl(url: string): Observable<string> {
        return this._httpClient.get(url, { responseType: 'text' });
    }

    /** V2 reports only: editable content (title/hero_intro/sections) plus a concurrency fingerprint. */
    getReportV2Source(reportId: string): Observable<{
        report_id: string;
        title: string;
        hero_intro: string;
        sections: { id: string; html: string }[];
        finalized: boolean;
        contentHash: string;
    }> {
        return this._httpClient
            .get<{ data: any }>(this.apiLink + `report/get-report-v2-source/${reportId}`)
            .pipe(map(res => res.data));
    }

    /** Every field optional — only what's provided changes. `expectedContentHash` guards against a stale edit. */
    editReportV2(
        reportId: string,
        patch: {
            title?: string;
            hero_intro?: string;
            sections?: { id: string; html: string }[];
            expectedContentHash?: string;
        }
    ): Observable<{ report_id: string }> {
        return this._httpClient
            .put<{ data: { report_id: string } }>(
                this.apiLink + `report/edit-report-v2/${reportId}`,
                patch
            )
            .pipe(map(res => res.data));
    }

    /** Lock/unlock a V2 report's content (finalized reports can no longer be edited). */
    setReportFinalized(
        reportId: string,
        finalized: boolean
    ): Observable<{ report_id: string; finalized: boolean }> {
        return this._httpClient
            .put<{ data: { report_id: string; finalized: boolean } }>(
                this.apiLink + `report/finalize-report/${reportId}`,
                { finalized }
            )
            .pipe(map(res => res.data));
    }

    getOneReport(id: any): Observable<Report> {
        let params = new HttpParams();
        params = params.append('report_id', id)
        return this._httpClient.get<Report>(this.apiLink + 'report/get-one-report', { params }).pipe(
            map((res: any) => {
                const data = (res?.data ?? {}) as Record<string, unknown>;
                const report = (data['report'] ?? data) as Record<string, unknown>;
                if (!report || typeof report !== 'object') {
                    return null as unknown as Report;
                }
                // Link fields sometimes sit on the data envelope beside `report`.
                return {
                    ...report,
                    sessionId:
                        report['sessionId'] ??
                        report['session_id'] ??
                        data['sessionId'] ??
                        data['session_id'],
                    linked_id:
                        report['linked_id'] ??
                        report['linkedId'] ??
                        data['linked_id'] ??
                        data['linkedId'],
                    chatIds:
                        report['chatIds'] ??
                        report['chat_ids'] ??
                        data['chatIds'] ??
                        data['chat_ids'],
                    questionnaireIds:
                        report['questionnaireIds'] ?? data['questionnaireIds'],
                    messageId:
                        report['messageId'] ??
                        report['message_id'] ??
                        data['messageId'] ??
                        data['message_id'],
                    hasChart: report['hasChart'] ?? data['hasChart'],
                    hasData: report['hasData'] ?? data['hasData'],
                } as Report;
            }),
        );
    }

    generateReportForInsights(modal) {
        return this._httpClient.post(this.apiLink + 'report/generate', modal)

    }


    getReportsData(id: string, type) {
        let params = new HttpParams().append('reportId', id)
        if (type) {
            params = params.append('reportType', type)
        }
        return this._httpClient.get(this.apiLink + 'report/get-report-data', { params })
    }
    generateReport(modal) {
        return this._httpClient.post(this.apiLink + 'report/generate', modal)
    }

    create(modal: any) {
        return this._httpClient.post(this.apiLink + 'departments/new-department', modal)
    }

    update(modal: any, id: number) {
        return this._httpClient.put(this.apiLink + `departments/${id}/edit-department`, modal)
    }

    delete(id: number) {
        const params = new HttpParams().append('reportId', id)
        return this._httpClient.delete(this.apiLink + 'report/delete-report', { params })
    }

    sendEmail(modal) {
        return this._httpClient.post(this.apiLink + 'report/send-report', modal)
    }


    ignoreQueries(reportId, modal) {
        return this._httpClient.put(this.apiLink + `report/ignore-queries/${reportId}`, modal)
    }


    editReport(reportId, modal) {
        return this._httpClient.put(this.apiLink + `report/edit-report/${reportId}`, modal)
    }

    updateChartTitle(modal) {
        return this._httpClient.put(this.apiLink + 'report/update-chart-title', modal)
    }

    shareReport(body: {
        reportId: string;
        memberIds: number[];
        staffIds: number[];
        userIds: number[];
    }) {
        return this._httpClient.put(this.apiLink + 'report/share-report', body);
    }

}

